import { Module } from 'vuex'
import { StateInterface } from '@/store'
import { BaseEntry, EntryState } from '@/models/interfaces/content-type'
import { EntryFieldSpec, EntryModel, ServiceResponse } from '@Protium/vue-ozone-utils'
import _ from 'lodash'
import { $castArray, $get, $setTo, guid } from '.'
import { models } from '@/store/modules/content'

export const PRIMARY_KEY_PATH = 'sys.id'

interface ContentModuleOptions {
  import?: boolean
}

export default function ContentModule<T extends BaseEntry> (
  type: string,
  handlerGetterRef: string,
  modelDefault?: EntryModel,
  state?, mutations?, getters?, actions?, options: ContentModuleOptions = {}
): Module<EntryState<T>, StateInterface> {
  try {
    if (!type) {
      throw Error('ContentModule: Missing content type')
    }
    if (!handlerGetterRef) {
      throw Error('ContentModule: Missing RequestHandler store getter name/path')
    }
    return {
      namespaced: true,
      state: {
        items: [],
        model: modelDefault || {},
        multi: false,
        selection: [],
        active: null,
        loading: false,
        ...state
      },
      mutations: {
        SetItems: (state: EntryState<T>, items: Array<T>) => {
          if (!items.length) state.items.length = 0
          state.items = items
        },
        Add: (state: EntryState<T>, entry: T) => {
          state.items = [entry, ...state.items]
        },
        Remove: (state: EntryState<T>, entry: T | Array<T>) => {
          const items = Array.isArray(entry) ? entry : [entry]
          items.forEach(e => {
            const index = state.items.findIndex(o => $get(o, PRIMARY_KEY_PATH) === $get(e, PRIMARY_KEY_PATH))
            if (index >= 0) state.items.splice(index, 1)
          })
        },
        SetEntryModel: (state: EntryState<T>, entryModel: EntryModel) => {
          state.model = entryModel
        },
        SetActive: (state: EntryState<T>, active: T) => {
          state.active = !state.multi ? active : (!Array.isArray(state.active) ? [active] : $setTo(state.active, active))
        },
        ToggleMulti: (state: EntryState<T>, value) => {
          state.multi = (value ?? value) || !state.multi
        },
        SetSelection: (state: EntryState<T>, selection: Array<T>) => {
          state.selection = selection
        },
        AddSelection: (state: EntryState<T>, values: T | Array<T>) => {
          state.selection = $setTo(state.selection, values, PRIMARY_KEY_PATH)
        },
        RemoveSelection: (state: EntryState<T>, index: Array<number> | number) => {
          const items = Array.isArray(index) ? index : [index]
          items.forEach(i => state.selection.splice(i, 1))
        },
        ClearSelection: (state: EntryState<T>) => {
          state.selection.length = 0
        },
        ClearActiveItem: (state: EntryState<T>) => {
          state.active = null
        },
        SetLoading: (state: EntryState<T>, loading: boolean) => {
          state.loading = loading
        },
        ...mutations
      },
      getters: {
        Options: () => ({ import: options.import }),
        Type: () => type,
        Items: (state: EntryState<T>) => state.items,
        // Note transforms the model value to retrieve the model for link / link-list fields
        Model: (state: EntryState<T>) => {
          const m = { ...(state.model || {}) }
          Object.keys(m).forEach(key => {
            if (['link', 'link-list'].includes(m[key].type) && m[key].contentType) {
              // const e:EntryFieldSpec = m[key]
              const e: any = m[key]
              e.model = models[m[key].contentType as string]
            }
          })

          return m
        },
        Multi: (state: EntryState<T>) => state.multi,
        ActiveItem: (state: EntryState<T>) => state.active,
        Selection: (state: EntryState<T>) => state.selection,
        Loading: (state: EntryState<T>) => state.loading,
        ...getters
      },
      actions: {
        // Socket_update: ({ commit, rootGetters }, payload) => {
        //   console.log('received socket update', payload)
        // },
        Fetch: ({ commit, rootGetters }, { query, config }): Promise<Array<ServiceResponse<T>>> => {
          // console.log('ContentModule/Fetch', type)
          if (!rootGetters[handlerGetterRef]) return Promise.reject(`Cannot fetch. Getter ${handlerGetterRef} did not return an initialized RequestHandler instance`)
          return new Promise((resolve, reject) => {
            commit('SetLoading', true)
            // console.log(`Fetching ${type}`, query, config)

            rootGetters[handlerGetterRef].get(type, query, config)
              .then(data => {
                // console.log('Fetched', data)

                commit('SetItems', data.items)

                resolve(data)
                commit('SetLoading', false)
              })
              .catch(error => {
                commit('SetLoading', false)
                reject(error)
              })
          })
        },
        Activate ({ commit }, entry) {
          commit('SetActive', entry)
        },
        SetMulti ({ commit }, value) {
          commit('ToggleMulti', value)
        },
        ReplaceSelection ({ commit }, selection) {
          commit('SetSelection', selection)
        },
        Select ({ state, commit }, values) {
          const additions = Array.isArray(values) ? values : [values]
          const splices: Array<number> = []
          additions.forEach(item => {
            const index = state.selection.findIndex(f => f.sys.id === item.sys.id)
            if (index >= 0) {
              additions.splice(index, 1)
              splices.push(index)
            }
          })

          commit('AddSelection', additions)
          commit('RemoveSelection', splices)
        },
        ClearSelection ({ commit }, entry) {
          commit('ClearSelection')
        },
        Remove ({ state, commit }, entry) {
          const items = Array.isArray(entry) ? entry : [entry]
          commit('Remove', entry)
          return state.items
        },
        Replace ({ state, commit, rootGetters }, payload, overwrite = false) {
          commit('SetItems', payload)

          return state.items
        },
        Set ({ state, commit, rootGetters }, payload: T | T[] | { data: T | T[], options: Record<string, boolean> }, partial = false) {
          // NOTE $setTo handles merging etc.
          const hasOptions = 'data' in payload && 'options' in payload
          const data = hasOptions ? payload.data : payload
          const options = hasOptions ? payload.options : {}
          const mapped = options?.partial
            ? $castArray(data).map(p => ({ ...(state.items.find(f => $get(f, PRIMARY_KEY_PATH) === $get(p, PRIMARY_KEY_PATH)) || {}), ...p }))
            : $castArray(data)

          const updated = $setTo(state.items || [], mapped.filter((f, i, a) => a.findIndex(fa => $get(fa, PRIMARY_KEY_PATH) === $get(f, PRIMARY_KEY_PATH)) === i), PRIMARY_KEY_PATH, options?.prepend)

          commit('SetItems', updated)
          return state.items
        },
        ...actions
      }
    }
  } catch (e) {
    throw Error(e as string)
  }
}
